import type { FC } from 'react';

import { useSession } from '@eneco/auth';
import { useSelfServiceAccount } from '@eneco/auth-be-self-service';
import {
  type ContractStatusDto,
  getEnecoBeXapiSiteApiV1AccountsByAccountNumberDeliveryAddressesByAddressIdentifierInvoicesInvoicePeriod,
  getEnecoBeXapiSiteApiV1AccountsByAccountNumberDeliveryAddressesByAddressIdentifierProducts,
  MonthlyInvoicePeriodDto,
} from '@eneco/dc-be/client';
import useAuthenticatedDCBE from '@eneco/dc-be/hooks/useAuthenticatedDCBE';
import mapScEnumToMap from '@eneco/dc-be/utils/mapScEnumToMap';
import unwrapData from '@eneco/dc-be/utils/unwrapData';
import { Bucket, TextLink } from '@eneco/sparky';
import { MyEnecoInvoiceBillingDetailsRendering } from '@sitecore/types/MyEnecoInvoiceBillingDetails';

import { BillingDetailsReadFormField } from './BillingDetailsReadFormField';
import DataErrorNotification from '../../../../../components/DataErrorNotification';
import ReadFormField from '../../../../../components/ReadFormField';
import { ADDRESS_PARAM } from '../../../../../utils/addSearchParams';
import { filterMetersWithYearlyInvoiceFrequency } from '../utils/filterMetersWithYearlyInvoiceFrequency';
import { groupMeterByBillingAccount } from '../utils/groupMeterByBillingAccount';

export interface InvoiceDueDateBucketProps {
  fields: MyEnecoInvoiceBillingDetailsRendering['fields']['dueDateAdvanceInvoices'];
  addressIdentifier: string | null | undefined;
  isLoadingAddress: boolean;
}

export const InvoiceDueDateBucket: FC<InvoiceDueDateBucketProps> = ({
  fields,
  addressIdentifier,
  isLoadingAddress,
}) => {
  const { selectedAccount } = useSelfServiceAccount();
  const { data: session } = useSession();
  const { isCurrentAccountReader } = useSelfServiceAccount();

  const {
    data: invoicesData,
    isLoading: isLoadingInvoices,
    error: errorInvoices,
  } = useAuthenticatedDCBE(
    getEnecoBeXapiSiteApiV1AccountsByAccountNumberDeliveryAddressesByAddressIdentifierInvoicesInvoicePeriod,
    {
      path: {
        accountNumber: selectedAccount.crmAccountNumber,
        addressIdentifier: addressIdentifier ?? '',
      },
    },
    [`/accounts/${selectedAccount.crmAccountNumber}/delivery-addresses/${addressIdentifier}/invoices/invoice-period`],
    session,
  );

  const {
    data: invoiceFrequencyData,
    isLoading: isLoadingInvoiceFrequency,
    error: errorInvoiceFrequency,
  } = useAuthenticatedDCBE(
    getEnecoBeXapiSiteApiV1AccountsByAccountNumberDeliveryAddressesByAddressIdentifierProducts,
    {
      path: {
        accountNumber: selectedAccount.crmAccountNumber,
        addressIdentifier: addressIdentifier ?? '',
      },
      query: {
        // https://dev.azure.com/enecomanagedcloud/Eneco%20Belgium/_workitems/edit/880738
        contractStatuses: ['Active', 'TerminationRequested', 'Terminated'] as ContractStatusDto[],
      },
    },
    addressIdentifier
      ? [`/accounts/${selectedAccount.crmAccountNumber}/delivery-addresses/${addressIdentifier}/products`]
      : null,
    session,
  );

  const isLoading = isLoadingInvoiceFrequency || isLoadingAddress || isLoadingInvoices;

  const billingData = unwrapData(invoicesData);
  const invoiceFrequency = unwrapData(invoiceFrequencyData);

  const billingDataFilteredOnlyYearlyMeters = filterMetersWithYearlyInvoiceFrequency(
    billingData?.meters,
    invoiceFrequency,
    true,
  );

  const metersPerBillingAccount = groupMeterByBillingAccount(billingDataFilteredOnlyYearlyMeters);

  const fetchingInvoicesError = errorInvoices !== undefined && !billingData;
  const fetchingInvoiceFrequencyError = errorInvoiceFrequency !== undefined && !invoiceFrequency;

  if (invoicesData?.error?.errors?.status === 404 || invoiceFrequencyData?.error?.errors?.status === 404) {
    return null;
  }

  if (!isLoading && (fetchingInvoicesError || fetchingInvoiceFrequencyError)) {
    return <DataErrorNotification />;
  }

  return (
    metersPerBillingAccount &&
    metersPerBillingAccount.length > 0 && (
      <Bucket title={fields.title.value}>
        <Bucket.Content>
          {isLoading && <ReadFormField isLoading={isLoading} isLoadingLabel={isLoading} value={''} label={''} />}
          {metersPerBillingAccount.map(([billingAccountNumber, meters]) => {
            const invoiceRangeMap = mapScEnumToMap<MonthlyInvoicePeriodDto>(fields.invoiceRangeList.value);
            const value = invoiceRangeMap.get(meters[0].period) ?? `${meters[0].period} [not in Sitecore]`;

            return <BillingDetailsReadFormField key={billingAccountNumber} meters={meters} value={value} />;
          })}
          {!isCurrentAccountReader &&
            invoiceFrequency?.activeProducts &&
            invoiceFrequency?.activeProducts.length > 0 && (
              <TextLink emphasis="high" href={`${fields.updateLink.value.href}?${ADDRESS_PARAM}=${addressIdentifier}`}>
                {fields.updateLink.value.text}
              </TextLink>
            )}
        </Bucket.Content>
      </Bucket>
    )
  );
};
